import { useRef, useState } from "react";
import { Paperclip, Upload, Trash2, Download, FileText, Eye, Pencil, Check, X } from "lucide-react";
import api from "@/lib/api";

const ALLOWED_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.tif,.tiff,.gif,.bmp,.webp,.eml,.msg,.txt,.csv,.zip,.rar,.7z,.stp,.step,.igs,.iges,.stl,.x_t,.x_b,.xmt_txt,.prt,.sldprt,.sldasm,.asm,.catpart,.catproduct,.ipt,.iam,.3dxml,.jt,.sat,.obj,.3mf,.dwg,.dxf,.model,.par,.psm,.dft,.3ds,.fbx,.glb,.gltf";

// File types a browser can render inline (so a "View" button makes sense).
const VIEWABLE_EXTS = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "txt", "csv"];
function isViewable(f) {
  const name = (f.original_filename || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  const ct = (f.content_type || "").toLowerCase();
  return VIEWABLE_EXTS.includes(ext) || ct.startsWith("image/") || ct === "application/pdf" || ct.startsWith("text/");
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function MiscFilesPanel({ entityType, entityId, files, canEdit, onChange }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [renamingId, setRenamingId] = useState(null);  // file_id currently being renamed
  const [renameValue, setRenameValue] = useState("");

  const list = files || [];

  function startRename(f) {
    setRenamingId(f.file_id);
    // Pre-fill without the extension so users edit just the name.
    const n = f.original_filename || "";
    setRenameValue(n.includes(".") ? n.slice(0, n.lastIndexOf(".")) : n);
  }

  async function saveRename(fileId) {
    const name = (renameValue || "").trim();
    if (!name) { setRenamingId(null); return; }
    setBusy(true);
    setError("");
    try {
      await api.patch(`/misc-files/${entityType}/${entityId}/${fileId}`, null, { params: { new_name: name } });
      setRenamingId(null);
      onChange && onChange();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleUpload(eventFiles) {
    if (!entityId) {
      setError("Save the record first before adding attachments.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      for (const f of Array.from(eventFiles)) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/misc-files/${entityType}/${entityId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onChange && onChange();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDelete(fileId) {
    if (!window.confirm("Delete this attachment? This cannot be undone.")) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(`/misc-files/${entityType}/${entityId}/${fileId}`);
      onChange && onChange();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  function handleDownload(fileId) {
    const url = `${api.defaults.baseURL}/misc-files/${entityType}/${entityId}/${fileId}`;
    window.open(url, "_blank");
  }

  function handleView(fileId) {
    // inline=1 → the browser renders it in a new tab instead of downloading.
    const url = `${api.defaults.baseURL}/misc-files/${entityType}/${entityId}/${fileId}?inline=1`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="misc-files-panel">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip size={16} className="text-slate-600" />
          <span className="font-medium text-sm">Attachments</span>
          <span className="text-xs text-slate-400">({list.length}/20)</span>
        </div>
        {canEdit && (
          <label className="inline-flex items-center gap-1 px-3 py-1 border border-sky-300 text-sky-700 text-xs rounded cursor-pointer hover:bg-sky-50" data-testid="misc-files-upload-btn">
            <Upload size={12} /> {busy ? "Uploading…" : "Add files"}
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_ACCEPT}
              multiple
              className="hidden"
              disabled={busy || list.length >= 20}
              onChange={(e) => e.target.files && e.target.files.length > 0 && handleUpload(e.target.files)}
            />
          </label>
        )}
      </div>
      {error && <div className="px-4 py-2 text-xs text-red-700 bg-red-50 border-b border-red-200">{error}</div>}
      {list.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-slate-400">
          No attachments yet. {canEdit ? "Click 'Add files' to upload PDFs, Word, Excel, images, emails, 3D/CAD models (STP, STEP, IGS, STL, PRT, SLDPRT, CATPart, DWG, DXF…), etc. (max 50 MB each, 20 files total)." : "No one has uploaded anything to this record."}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.map((f) => (
            <li key={f.file_id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
              <FileText size={16} className="text-slate-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {renamingId === f.file_id ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(f.file_id); if (e.key === "Escape") setRenamingId(null); }}
                      className="flex-1 min-w-0 border border-slate-300 rounded px-2 py-1 text-sm"
                      placeholder="New name"
                      data-testid={`misc-file-rename-input-${f.file_id}`}
                    />
                    <button onClick={() => saveRename(f.file_id)} disabled={busy} className="text-emerald-600 hover:text-emerald-700 p-1 disabled:opacity-30" title="Save name" data-testid={`misc-file-rename-save-${f.file_id}`}>
                      <Check size={15} />
                    </button>
                    <button onClick={() => setRenamingId(null)} className="text-slate-400 hover:text-slate-600 p-1" title="Cancel">
                      <X size={15} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="truncate font-medium" title={f.original_filename}>{f.original_filename}</div>
                    <div className="text-[11px] text-slate-500">
                      {fmtSize(f.size_bytes)} · uploaded {fmtDate(f.uploaded_at)} by {f.uploaded_by_name || f.uploaded_by}
                    </div>
                  </>
                )}
              </div>
              {renamingId !== f.file_id && (
                <>
                  {isViewable(f) && (
                    <button onClick={() => handleView(f.file_id)} className="text-slate-500 hover:text-orange-600 p-1" title="View" data-testid={`misc-file-view-${f.file_id}`}>
                      <Eye size={14} />
                    </button>
                  )}
                  <button onClick={() => handleDownload(f.file_id)} className="text-sky-600 hover:text-sky-700 p-1" title="Download" data-testid={`misc-file-download-${f.file_id}`}>
                    <Download size={14} />
                  </button>
                  {canEdit && (
                    <button onClick={() => startRename(f)} disabled={busy} className="text-slate-400 hover:text-orange-600 p-1 disabled:opacity-30" title="Rename" data-testid={`misc-file-rename-${f.file_id}`}>
                      <Pencil size={14} />
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => handleDelete(f.file_id)} disabled={busy} className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-30" title="Delete" data-testid={`misc-file-delete-${f.file_id}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
